import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useErrorInfoModal } from '@features/modal'

interface SuggestOption {
  title: { text: string };
  subtitle?: { text: string };
  tags?: string[];
  distance?: { value: number; text: string };
}

interface UseYandexSuggestOptions {
  apiKey: string;
  lang?: 'ru_RU' | 'kk_KZ';
  coordinates?: [number, number];
  autoFetch?: boolean;
}

export const useYandexSuggest = ({
  apiKey,
  lang = 'ru_RU',
  coordinates,
  autoFetch = true,
}: UseYandexSuggestOptions) => {
  const [ query, setQuery ] = useState('')
  const [ suggestions, setSuggestions ] = useState<SuggestOption[]>([])
  const [ loading, setLoading ] = useState(false)
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()

  const sessionTokenRef = useRef<string>(crypto.randomUUID())

  const fetchSuggestions = async (text: string) => {
    if (!text.trim()) {
      setSuggestions([])
      return
    }

    setLoading(true)

    try {
      const params = new URLSearchParams({
        text,
        lang,
        sessionToken: sessionTokenRef.current,
        apikey: apiKey,
        results: '10',
        types: 'street,district,locality,house,province',
      })

      if (coordinates) {
        params.append('ull', `${coordinates[0]},${coordinates[1]}`)
      }

      const res = await fetch(
        `https://suggest-maps.yandex.ru/v1/suggest?${params.toString()}`,
      )

      const data = await res.json()
      setSuggestions(data.results || [])
    }
    catch (error) {
      console.error('Failed to fetch suggestions', error)
      openErrorInfoModal({
        description: te('fetch_suggestions'),
      })
    }
    finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!autoFetch) return

    const controller = new AbortController()

    fetchSuggestions(query)

    return () => controller.abort()
  }, [ query, lang, apiKey, coordinates, autoFetch ])

  const clearSuggestions = () => {
    setSuggestions([])
  }

  return {
    query,
    setQuery,
    suggestions,
    loading,
    clearSuggestions,
    fetchSuggestions,
  }
}
